'use client';

import { AnimatePresence, motion } from 'framer-motion';
import { useTranslations } from 'next-intl';
import { useEffect, useMemo, useState } from 'react';
import type { RelatedEvent, RelatedNews } from '@/lib/api/event/models';
import { Empty } from '@/lib/components/Empty';
import { RelatedEventRow } from '@/lib/components/Event/RelatedEventRow';
import { RelatedNewsRow } from '@/lib/components/Event/RelatedNewsRow';
import { UnderlineTabs } from '@/lib/ui-kit-v2/underline-tabs';
import { preloadImages } from '@/lib/utils/preloadImage';

const NEWS_COLLAPSED_COUNT = 3;
const EVENTS_COLLAPSED_COUNT = 3;

interface EventRelatedTabsProps {
  relatedNews?: RelatedNews[];
  relatedEvents?: RelatedEvent[];
}

export function EventRelatedTabs({
  relatedNews = [],
  relatedEvents: relatedEventsData = [],
}: EventRelatedTabsProps) {
  const t = useTranslations('event.tabs');
  const [activeTab, setActiveTab] = useState('events');
  const [newsExpanded, setNewsExpanded] = useState(false);
  const [eventsExpanded, setEventsExpanded] = useState(false);

  const TABS = useMemo(
    () => [
      { id: 'events', label: t('relatedEvents') },
      { id: 'news', label: t('relatedNews') },
    ],
    [t],
  );

  useEffect(() => {
    preloadImages(
      relatedNews.map((news) => news.imageLink),
      'low',
    );
  }, [relatedNews]);

  useEffect(() => {
    preloadImages(
      relatedEventsData.slice(EVENTS_COLLAPSED_COUNT).map((event) => event.image),
      'low',
    );
  }, [relatedEventsData]);

  return (
    <div className="border-b border-solid border-v2-border pb-6">
      <UnderlineTabs
        tabs={TABS}
        activeTab={activeTab}
        onTabChange={(tabId) => {
          setActiveTab(tabId);
          setNewsExpanded(false);
          setEventsExpanded(false);
        }}
      />

      <div className="mt-4 min-h-[230px]">
        <AnimatePresence mode="wait">
          {activeTab === 'events' && (
            <motion.div
              key="events"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
              className="flex flex-col"
            >
              {relatedEventsData.length > 0 ? (
                <>
                  {(eventsExpanded
                    ? relatedEventsData
                    : relatedEventsData.slice(0, EVENTS_COLLAPSED_COUNT)
                  ).map((event) => (
                    <RelatedEventRow key={event.id} event={event} />
                  ))}
                  {relatedEventsData.length > EVENTS_COLLAPSED_COUNT && (
                    <button
                      type="button"
                      onClick={() => setEventsExpanded((prev) => !prev)}
                      className="mt-1 w-fit cursor-pointer whitespace-nowrap text-[13px] font-normal leading-4 tracking-[-0.13px] text-v2-text-secondary transition-colors hover:text-v2-text-primary"
                    >
                      {eventsExpanded ? t('viewLess') : t('viewMore')}
                    </button>
                  )}
                </>
              ) : (
                <div className="flex min-h-[230px] items-center justify-center">
                  <Empty title={t('noRelatedEventsTitle')} desc={t('noRelatedEventsDesc')} />
                </div>
              )}
            </motion.div>
          )}

          {activeTab === 'news' && (
            <motion.div
              key="news"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
              className="flex flex-col"
            >
              {relatedNews.length > 0 ? (
                <div className="flex flex-col gap-2">
                  <div className="flex flex-col gap-2">
                    {(newsExpanded ? relatedNews : relatedNews.slice(0, NEWS_COLLAPSED_COUNT)).map(
                      (news) => (
                        <RelatedNewsRow key={news.id} news={news} />
                      ),
                    )}
                  </div>
                  {relatedNews.length > NEWS_COLLAPSED_COUNT && (
                    <button
                      type="button"
                      onClick={() => setNewsExpanded((prev) => !prev)}
                      className="w-fit cursor-pointer whitespace-nowrap text-[13px] font-normal leading-4 tracking-[-0.13px] text-v2-text-secondary transition-colors hover:text-v2-text-primary"
                    >
                      {newsExpanded ? t('viewLess') : t('viewMore')}
                    </button>
                  )}
                </div>
              ) : (
                <div className="flex min-h-[230px] items-center justify-center">
                  <Empty title={t('noRelatedNewsTitle')} desc={t('noRelatedNewsDesc')} />
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
